import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';
import {
  PlayCircleOutline,
  AutoAwesome,
  CloudUpload,
  Analytics,
  Schedule,
  SmartDisplay,
  Speed,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';

const About: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <AutoAwesome />,
      title: 'AI-Powered Content Generation',
      description: 'Our advanced GPT integration creates compelling scripts tailored to your niche, ensuring each reel captures attention and drives engagement.',
      color: theme.palette.primary.main,
    },
    {
      icon: <SmartDisplay />,
      title: 'Automated Video Assembly',
      description: 'Seamlessly combine AI-generated scripts with high-quality stock footage from Pexels, creating professional-looking reels in minutes.',
      color: theme.palette.secondary.main,
    },
    {
      icon: <CloudUpload />,
      title: 'Multi-Platform Publishing',
      description: 'Publish your content across Instagram, Facebook, YouTube Shorts, TikTok, and X (Twitter) with a single click.',
      color: theme.palette.success.main,
    },
    {
      icon: <Schedule />,
      title: 'Smart Scheduling',
      description: 'Schedule your content for optimal engagement times and enable full automation mode for continuous content creation.',
      color: theme.palette.info.main,
    },
    {
      icon: <Analytics />,
      title: 'Advanced Analytics',
      description: 'Track performance across all platforms with detailed insights, engagement metrics, and growth analytics.',
      color: theme.palette.warning.main,
    },
    {
      icon: <Speed />,
      title: 'Lightning Fast Processing',
      description: 'Generate complete reels in under 2 minutes with our optimized AI infrastructure and automated workflows.',
      color: theme.palette.error.main,
    },
  ];

  const stats = [
    { value: '10K+', label: 'Reels Created' },
    { value: '50+', label: 'Active Creators' },
    { value: '5', label: 'Platforms Supported' },
    { value: '99.9%', label: 'Uptime' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      <Box sx={{ height: 72 }} />
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <PlayCircleOutline
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: 'primary.main',
                mb: 3,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3,
              }}
            >
              About AutoReelr
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                mb: 4,
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              AutoReelr is the ultimate AI-powered platform for creating and managing faceless reels across all major social media platforms. We empower content creators to scale their presence with automation, intelligence, and efficiency.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mb: 6 }}
            >
              <Chip
                label="AI-First"
                variant="outlined"
                sx={{ color: 'primary.main', borderColor: 'primary.main' }}
              />
              <Chip
                label="Automated"
                variant="outlined"
                sx={{ color: 'secondary.main', borderColor: 'secondary.main' }}
              />
              <Chip
                label="Multi-Platform"
                variant="outlined"
                sx={{ color: 'success.main', borderColor: 'success.main' }}
              />
              <Chip
                label="Analytics-Driven"
                variant="outlined"
                sx={{ color: 'info.main', borderColor: 'info.main' }}
              />
            </Stack>
          </Box>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Card
            sx={{
              mb: 8,
              p: 4,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Our Mission
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                lineHeight: 1.8,
                maxWidth: 800,
                mx: 'auto',
              }}
            >
              To democratize content creation by providing creators with AI-powered tools that eliminate the complexity of video production while maximizing reach and engagement across all social media platforms.
            </Typography>
          </Card>
        </motion.div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                mb: 2,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              What Makes Us Different
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 6,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Advanced AI technology meets intuitive design to deliver unparalleled content creation experiences
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 12px 40px rgba(102, 126, 234, 0.25)`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            color: feature.color,
                          }}
                        >
                          {React.cloneElement(feature.icon, { sx: { fontSize: 40, mr: 2 } })}
                          <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                            {feature.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                mb: 6,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Platform Statistics
            </Typography>
            <Grid container spacing={4}>
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Card
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Built with Cutting-Edge Technology
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              AutoReelr leverages the latest in AI, cloud computing, and web technologies to deliver exceptional performance and reliability.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Chip label="OpenAI GPT" variant="outlined" />
              <Chip label="Azure TTS" variant="outlined" />
              <Chip label="Pexels API" variant="outlined" />
              <Chip label="FFmpeg" variant="outlined" />
              <Chip label="React" variant="outlined" />
              <Chip label="Cloudflare Workers" variant="outlined" />
              <Chip label="Celery" variant="outlined" />
              <Chip label="Redis" variant="outlined" />
            </Stack>
          </Card>
        </motion.div>
      </Container>

      <Footer />
    </Box>
  );
};

export default About;
